import type { MouseEventHandler } from 'react';
import { BEMBlock } from 'helpers/classnames';
import { Button } from 'palette/Button';
import { TextLink } from 'palette/TextLink';
import { useHeaderTopContext } from 'palette/HeaderTop/context';
import { Header, Small, Typography } from 'palette/Typography';
import { getNavigationURL } from './helpers';

import './styles/Promobar.scss';

interface PromoBarProps {
    onOpen(): void;
    onClose(): void;
    onNextClick(): void;
    onPreviousClick(): void;
    onMainLinkClick: MouseEventHandler<HTMLElement>;
    onExpandedLinkClick: MouseEventHandler<HTMLElement>;
    ariaLabel?: {
        leftArrowButton: string;
        rightArrowButton: string;
    };
}

export function PromoBar({
    onOpen,
    onClose,
    ariaLabel,
    onNextClick,
    onPreviousClick,
    onMainLinkClick,
    onExpandedLinkClick
}: PromoBarProps) {
    const {
        isExpanded,
        nextBanner,
        activeSlide,
        stopInterval,
        startInterval,
        setIsExpanded,
        previousBanner,
        hasMultipleBanners,
    } = useHeaderTopContext();

    const [block, element] = BEMBlock('palette-promobar-v2', {
        expanded: isExpanded,
    });

    const {
        color,
        title,
        subtitle,
        is_compact,
        navigation,
        expandable,
        extra_subtitle,
        background: backgroundColor
    } = activeSlide || {};
    const hasExpandableContent = !!expandable;
    const mainNavigationURL = getNavigationURL(navigation ?? undefined);

    const handlePreviousClick = () => {
        previousBanner();
        onPreviousClick();
    };

    const handleNextClick = () => {
        nextBanner();
        onNextClick();
    };

    const handlePromoBarExpand = () => {
        setIsExpanded(true);
        stopInterval();
        onOpen();
    };

    const handlePromoBarCollapse = () => {
        setIsExpanded(false);
        startInterval();
        onClose();
    };

    const handleMainLinkClick: MouseEventHandler<HTMLElement> = (event) => {
        onMainLinkClick(event);
    };

    const handleExpandedLinkClick: MouseEventHandler<HTMLElement> = (event) => {
        onExpandedLinkClick(event);
    };

    const bannerContent = (
        <Small
            className={element('slide-texts', {
                'is-compact': is_compact
            })}
            color="inherit"
            level="2"
        >
            <Typography transform="uppercase" weight="bold">{title}</Typography>
            <Typography>{subtitle}</Typography>
            <Typography>{extra_subtitle}</Typography>
        </Small>
    );

    return (
        <>
            <div
                className={block}
                style={{ backgroundColor, color }}
            >
                <div className={element('slide-container')}>
                    {hasMultipleBanners && (
                        <Button
                            isTransparent
                            ariaLabel={ariaLabel?.leftArrowButton}
                            className={element('previous-button')}
                            iconColor={color}
                            iconName="palette_chevron_left"
                            onClick={handlePreviousClick}
                        />
                    )}

                    <div
                        className={element('slide')}
                        {...hasExpandableContent && { onClick: handlePromoBarExpand }}
                    >
                        {mainNavigationURL ? (
                            <TextLink
                                href={mainNavigationURL}
                                onClick={handleMainLinkClick}
                                onContextMenu={handleMainLinkClick}
                            >
                                {bannerContent}
                            </TextLink>
                        ) : bannerContent}
                    </div>

                    {hasMultipleBanners && (
                        <Button
                            isTransparent
                            ariaLabel={ariaLabel?.rightArrowButton}
                            className={element('next-button')}
                            iconColor={color}
                            iconName="palette_chevron_right"
                            onClick={handleNextClick}
                        />
                    )}

                    <Button
                        isTransparent
                        className={element('close-button')}
                        iconColor={color}
                        iconName="palette_close"
                        onClick={handlePromoBarCollapse}
                    />
                </div>

                {isExpanded && expandable && (
                    <div className={element('expanded-menu')}>
                        <div className={element('expanded-menu-title')}>
                            {expandable.content_title && (
                                <Header color="inherit" level="2">{expandable.content_title}</Header>
                            )}
                            {expandable.content_description && (
                                <Typography>{expandable.content_description}</Typography>
                            )}
                        </div>

                        {expandable.category_grid && (
                            <ul className={element('expanded-menu-links-container')}>
                                {expandable.category_grid.map((item) => {
                                    const categoryNavigationURL = getNavigationURL(item.navigation);
                                    return (
                                        <li className={element('expanded-menu-link-item')} key={item.id}>
                                            <Button
                                                isExpanded
                                                theme="secondary"
                                                href={categoryNavigationURL}
                                                onClick={handleExpandedLinkClick}
                                                onContextMenu={handleExpandedLinkClick}
                                            >
                                                {item.text}
                                            </Button>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}

                        {expandable.disclaimer_text && (
                            <Typography>{expandable.disclaimer_text}</Typography>
                        )}
                    </div>
                )}
            </div>

            {isExpanded && (
                <div
                    className={element('overlay')}
                    onClick={handlePromoBarCollapse}
                />
            )}
        </>
    );
}
